import React, { useState } from 'react';
import { RefreshCw, Plus, X } from 'lucide-react';
import { Post, PostCategory } from '../services/postsApi';
import { usePostFeed } from '../hooks/usePostFeed';
import PostCard from './PostCard';
import AdSlot from './AdSlot';
import { GeoLocation } from '../types';

const FILTERS: Array<{ value: PostCategory | null; label: string }> = [
  { value: null,              label: 'All' },
  { value: 'police_activity', label: 'Police' },
  { value: 'shooting',        label: 'Shooting' },
  { value: 'fire_ems',        label: 'Fire/EMS' },
  { value: 'traffic',         label: 'Traffic' },
  { value: 'pursuit',         label: 'Pursuit' },
  { value: 'containment',     label: 'Containment' },
  { value: 'suspicious',      label: 'Suspicious' },
  { value: 'community',       label: 'Community' },
];

interface Props {
  isSignedIn: boolean;
  isAdmin?: boolean;
  selfUid?: string | null;
  userLocation?: GeoLocation | null;
  onClose: () => void;
  onCompose: () => void;
  onLocate?: (lat: number, lng: number) => void;
  onSignInRequired?: () => void;
}

const AD_INTERVAL = 8;

export default function Feed({
  isSignedIn, isAdmin, selfUid, userLocation, onClose, onCompose, onLocate, onSignInRequired,
}: Props) {
  const [filter, setFilter] = useState<PostCategory | null>(null);
  const { posts, loading, error, refresh, patchLocal, removeLocal } =
    usePostFeed({ category: filter, enabled: true });

  return (
    <aside className="absolute top-0 right-0 h-full w-full sm:w-[420px] bg-slate-950/85 backdrop-blur-md border-l border-amber-700/30 z-30 flex flex-col">
      <header className="flex items-center gap-2 p-3 border-b border-slate-800">
        <h2 className="text-amber-200 font-bold tracking-wide flex-1">COMMUNITY FEED</h2>
        <button onClick={refresh} className="p-1.5 rounded hover:bg-slate-800 text-slate-400" title="Refresh"
          aria-label="Refresh"><RefreshCw size={14} className={loading ? 'animate-spin' : ''}/></button>
        <button onClick={onCompose} className="px-2 py-1 rounded bg-amber-600 hover:bg-amber-500 text-slate-950 font-bold text-xs inline-flex items-center gap-1">
          <Plus size={12}/> POST
        </button>
        <button onClick={onClose} className="p-1.5 rounded hover:bg-slate-800 text-slate-400" aria-label="Close feed"><X size={14}/></button>
      </header>

      <div className="flex gap-1 p-2 overflow-x-auto border-b border-slate-800/60 scrollbar-thin">
        {FILTERS.map(f => (
          <button key={f.label} onClick={() => setFilter(f.value)}
            className={`px-2 py-1 rounded text-xs whitespace-nowrap border ${
              filter === f.value
                ? 'bg-amber-600/30 text-amber-200 border-amber-500/50'
                : 'border-slate-700 text-slate-400 hover:text-slate-200 hover:bg-slate-800'
            }`}>{f.label}</button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto p-3">
        {error && <div className="text-xs text-red-300 bg-red-900/30 border border-red-800 rounded px-2 py-1 mb-2">{error}</div>}
        {posts.length === 0 && !loading && (
          <div className="text-center text-slate-500 text-sm py-8">
            No posts yet. {isSignedIn ? 'Be the first to report.' : 'Sign in to be the first to report.'}
          </div>
        )}
        {posts.map((p, i) => (
          <React.Fragment key={p.id}>
            <PostCard
              post={p}
              isSignedIn={isSignedIn}
              isAdmin={isAdmin}
              selfUid={selfUid}
              onPatch={patchLocal}
              onRemove={removeLocal}
              onLocate={onLocate}
              onSignInRequired={onSignInRequired}
            />
            {/* Native ad slot every Nth card. Server returns null for premium
                viewers, no campaigns matching, or if frequency cap is hit. */}
            {((i + 1) % AD_INTERVAL === 0) && (
              <AdSlot slot="feed_native" userLocation={userLocation} variant="feed"/>
            )}
          </React.Fragment>
        ))}
      </div>
    </aside>
  );
}
